import { useEffect, useMemo, useState } from "react";
import { format, addMonths, startOfMonth, endOfMonth } from "date-fns";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { Sun, Sunset, CheckCircle2, CalendarDays } from "lucide-react";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const fmt = (d) => format(d, "yyyy-MM-dd");

const initial = { name: "", address: "", phone: "", email: "", service: "pressure_washing", notes: "", website: "" };

export default function Booking() {
  useDocumentMeta({
    title: "Book a Slot | Pressure Washing & LED Lighting Rangiora",
    description: "Pick a morning or afternoon slot to have your driveway pressure-washed or LED pathway lighting installed. Locally owned, servicing Rangiora & North Canterbury.",
  });
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const [month, setMonth] = useState(startOfMonth(today));
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [availability, setAvailability] = useState({}); // {dateStr: ["morning","afternoon"]}
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    const start = fmt(startOfMonth(month));
    const end = fmt(endOfMonth(addMonths(month, 1)));
    api.get("/bookings/availability", { params: { start, end } }).then(({ data }) => {
      const map = {};
      data.forEach((a) => { map[a.date] = a.booked_slots; });
      setAvailability(map);
    }).catch(() => {});
  }, [month, confirmed]);

  const selectedDateStr = date ? fmt(date) : null;
  const bookedForDate = selectedDateStr ? availability[selectedDateStr] || [] : [];
  const fullyBookedDays = useMemo(() => {
    return Object.entries(availability)
      .filter(([, s]) => s.length >= 2)
      .map(([d]) => { const [y, m, dd] = d.split("-").map(Number); return new Date(y, m - 1, dd); });
  }, [availability]);

  const isWeekend = (d) => { const wd = d.getDay(); return wd === 0; }; // Sundays off

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !slot) { toast.error("Pick a date and slot first."); return; }
    if (!form.name.trim() || !form.address.trim() || !form.phone.trim()) {
      toast.error("Please fill in name, address, and phone."); return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        address: form.address.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        service: form.service,
        booking_date: selectedDateStr,
        slot,
        notes: form.notes.trim() || null,
        website: form.website, // honeypot
      };
      const { data } = await api.post("/bookings", payload);
      setConfirmed(data);
      toast.success("Booking confirmed!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't lock that slot. Try another.");
    } finally {
      setLoading(false);
    }
  };

  if (confirmed) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 sm:py-24" data-testid="booking-success">
        <Card className="rounded-3xl border-gray-100 shadow-sm">
          <CardContent className="p-10 text-center">
            <CheckCircle2 className="w-12 h-12 text-[#0369A1] mx-auto" />
            <h1 className="font-heading text-3xl font-bold mt-4">Booking confirmed</h1>
            <p className="mt-3 text-[#4B5563]">Thanks {confirmed.name?.split(" ")[0]} - we&apos;ll see you on <strong>{confirmed.booking_date}</strong> ({confirmed.slot}).</p>
            <Button onClick={() => { setConfirmed(null); setDate(null); setSlot(null); setForm(initial); }} className="mt-8 rounded-full bg-[#0369A1] hover:bg-[#0284C7]" data-testid="booking-make-another">
              Make another booking
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <section className="border-b border-gray-100 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12 sm:py-16" data-testid="booking-header">
          <div className="text-xs uppercase tracking-widest text-[#0369A1] font-semibold">Booking</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#111827] mt-3 leading-[1.04]">
            Lock in a <span className="glow-underline">morning</span> or afternoon.
          </h1>
          <p className="mt-4 text-[#4B5563] max-w-2xl">One booking per slot per day - you&apos;ll never get bumped or double-booked.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12 grid lg:grid-cols-5 gap-8">
        {/* Calendar + slots */}
        <Card className="lg:col-span-2 rounded-3xl border-gray-100 shadow-sm" data-testid="booking-calendar-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <CalendarDays className="w-4 h-4 text-[#0369A1]" />
              <div className="font-heading font-semibold">Pick a date</div>
            </div>
            <Calendar
              mode="single"
              selected={date}
              onSelect={(d) => { setDate(d); setSlot(null); }}
              month={month}
              onMonthChange={setMonth}
              disabled={[{ before: today }, isWeekend, ...fullyBookedDays]}
              fromDate={today}
              data-testid="booking-calendar"
            />
            <div className="mt-3 text-xs text-[#4B5563] space-y-1">
              <div>· Sundays are closed.</div>
              <div>· Mon-Fri are afternoon only (we&apos;re at school/work in the morning).</div>
              <div>· Saturdays: morning &amp; afternoon both available.</div>
              <div>· Greyed-out days are fully booked.</div>
            </div>

            {date && (
              <div className="mt-6">
                <div className="text-sm font-medium text-[#111827] mb-3">Slot for {format(date, "EEE, d MMM yyyy")}</div>
                <div className="grid grid-cols-2 gap-3">
                  {["morning", "afternoon"].map((s) => {
                    const isBooked = bookedForDate.includes(s);
                    const wd = date.getDay(); // 0=Sun, 1..5=Mon-Fri, 6=Sat
                    const isWeekdayMorning = s === "morning" && wd >= 1 && wd <= 5;
                    const isDisabled = isBooked || isWeekdayMorning;
                    const Icon = s === "morning" ? Sun : Sunset;
                    return (
                      <button
                        type="button"
                        key={s}
                        disabled={isDisabled}
                        onClick={() => setSlot(s)}
                        data-testid={`booking-slot-${s}`}
                        className={`rounded-2xl border p-4 text-left transition ${
                          isDisabled
                            ? "opacity-50 cursor-not-allowed border-gray-200 bg-gray-50"
                            : slot === s
                            ? "border-[#0369A1] bg-[#E0F2FE]"
                            : "border-gray-200 hover:border-[#0369A1] bg-white"
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${s === "morning" ? "text-[#0369A1]" : "text-[#D97706]"}`} />
                        <div className="font-medium mt-2 capitalize">{s}</div>
                        <div className="text-xs text-[#4B5563]">{s === "morning" ? "8am - 12pm" : "12pm - 5pm"}</div>
                        {isBooked && <div className="mt-1 text-[10px] uppercase tracking-widest text-red-600">Booked</div>}
                        {!isBooked && isWeekdayMorning && (
                          <div className="mt-1 text-[10px] uppercase tracking-widest text-[#4B5563]">Sat only</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Details form */}
        <Card className="lg:col-span-3 rounded-3xl border-gray-100 shadow-sm" data-testid="booking-form-card">
          <CardContent className="p-6 sm:p-8">
            <div className="font-heading font-semibold mb-1">Your details</div>
            <p className="text-sm text-[#4B5563] mb-5">We&apos;ll confirm by phone or email the day before.</p>
            <form onSubmit={submit} className="space-y-5" data-testid="booking-form">
              {/* Honeypot */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
                <label htmlFor="b-website">Website (leave empty)</label>
                <input id="b-website" type="text" tabIndex="-1" autoComplete="off" value={form.website} onChange={upd("website")} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="b-name">Full name *</Label>
                  <Input id="b-name" data-testid="booking-input-name" value={form.name} onChange={upd("name")} required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="b-phone">Phone *</Label>
                  <Input id="b-phone" data-testid="booking-input-phone" value={form.phone} onChange={upd("phone")} required inputMode="tel" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="b-email">Email (optional)</Label>
                <Input id="b-email" data-testid="booking-input-email" type="email" value={form.email} onChange={upd("email")} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="b-address">Job address *</Label>
                <Input id="b-address" data-testid="booking-input-address" value={form.address} onChange={upd("address")} required />
              </div>
              <div className="space-y-1.5">
                <Label>Service</Label>
                <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                  <SelectTrigger data-testid="booking-select-service"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pressure_washing">Pressure Washing</SelectItem>
                    <SelectItem value="led_lighting">LED Lighting</SelectItem>
                    <SelectItem value="package_deal">Combined Package Deal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="b-notes">Notes (optional)</Label>
                <Textarea id="b-notes" data-testid="booking-input-notes" value={form.notes} onChange={upd("notes")} rows={3} />
              </div>

              <div className="rounded-2xl bg-[#F3F4F6] p-4 text-sm" data-testid="booking-summary">
                <div className="text-xs uppercase tracking-widest text-[#4B5563]">Your slot</div>
                <div className="font-heading text-lg font-bold text-[#111827] mt-1">
                  {date && slot ? `${format(date, "EEE, d MMM yyyy")} - ${slot}` : "Pick a date & slot"}
                </div>
              </div>

              <Button type="submit" disabled={loading || !date || !slot} className="w-full rounded-full bg-[#0369A1] hover:bg-[#0284C7] py-6 text-base" data-testid="booking-submit">
                {loading ? "Booking..." : "Confirm booking"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
